import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    AlertTriangle,
    Calendar,
    Droplets,
    Flame,
    Hammer,
    Package,
    Pencil,
    Receipt,
    RefreshCw,
    Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
    computeAfterHoursRate,
    computeCrewLadder,
    computeReviewStatus,
    formatMaterialsLabel,
    formatTermMonths,
    type RateCardDraftContext,
} from "@/lib/rateCardPreview";

type TradeRow = { code: string; name: string };

function num(v: string | null | undefined): number {
    if (v == null || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function fmtDate(iso: string | null | undefined): string {
    if (!iso) return "—";
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 py-2 border-b border-border/40 last:border-0">
            <span className="text-sm text-muted-foreground">{label}</span>
            <span className="text-sm font-medium text-right">{value}</span>
        </div>
    );
}

export function RateCardSummary({
    ctx,
    tradesData,
    cityFeesCharged,
    adminFeeCharged,
    permitFeesCharged,
    splitFirelineMain,
    onEdit,
    onRenew,
    renewPending,
}: {
    ctx: RateCardDraftContext;
    tradesData: TradeRow[];
    cityFeesCharged: boolean;
    adminFeeCharged: boolean;
    /** The permit's own switch (migration 700). Undefined = charged. */
    permitFeesCharged?: boolean;
    splitFirelineMain: boolean;
    onEdit: () => void;
    onRenew: () => void;
    renewPending?: boolean;
}) {
    const pricing = ctx.pricing;
    const isTM = (pricing.billing_mode ?? "flat_rate") === "time_and_materials";
    const reviewStatus = computeReviewStatus(pricing);
    const hourly = num(pricing.hourly_labor_rate);
    const crewLadder = computeCrewLadder(hourly);
    const ah = computeAfterHoursRate(pricing);
    const tradeNames = ctx.trades.map((code) => tradesData.find((t) => t.code === code)?.name ?? code).join(", ");

    if (!isTM) {
        return (
            <Card className="border-border/60">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium">Billing</CardTitle>
                    <Button type="button" size="sm" variant="outline" onClick={onEdit}>
                        <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                    </Button>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-muted-foreground">Flat rate — standard pricebook pricing applies.</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {(reviewStatus === "due" || reviewStatus === "overdue") && (
                <div
                    className={cn(
                        "rounded-lg border px-4 py-3 flex flex-wrap items-center justify-between gap-3",
                        reviewStatus === "overdue"
                            ? "border-destructive/50 bg-destructive/10"
                            : "border-amber-500/50 bg-amber-500/10",
                    )}
                >
                    <div className="flex items-start gap-2">
                        <AlertTriangle
                            className={cn(
                                "h-5 w-5 shrink-0 mt-0.5",
                                reviewStatus === "overdue" ? "text-destructive" : "text-amber-600",
                            )}
                        />
                        <div>
                            <p className="text-sm font-semibold">
                                {reviewStatus === "overdue"
                                    ? "Rate agreement expired — review required"
                                    : "Rate agreement review due soon"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Current pricing remains in effect until you renew or adjust. Expires{" "}
                                {fmtDate(pricing.rate_expires_on)}.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={onRenew} disabled={renewPending}>
                            <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", renewPending && "animate-spin")} />
                            Renew
                        </Button>
                        <Button type="button" size="sm" onClick={onEdit}>
                            Adjust
                        </Button>
                    </div>
                </div>
            )}

            <Card className="border-border/60">
                <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
                    <CardTitle className="text-sm font-medium">Rate card summary</CardTitle>
                    <Button type="button" size="sm" variant="outline" onClick={onEdit}>
                        <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                    </Button>
                </CardHeader>
                <CardContent className="space-y-1">
                    <SummaryRow
                        label="Billing"
                        value={
                            <Badge variant="secondary" className="font-normal">
                                Contract (T&amp;M)
                            </Badge>
                        }
                    />
                    {ctx.trades.length > 0 && (
                        <SummaryRow
                            label="Services"
                            value={
                                <span className="inline-flex items-center gap-1">
                                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                    {tradeNames}
                                </span>
                            }
                        />
                    )}
                    {hourly > 0 && (
                        <SummaryRow
                            label="Labor"
                            value={
                                <span className="inline-flex items-center gap-1">
                                    <Hammer className="h-3.5 w-3.5 text-muted-foreground" />${hourly.toFixed(2)}/hr
                                    {crewLadder.length > 1 && (
                                        <span className="text-muted-foreground font-normal">
                                            · up to {crewLadder.length} techs
                                        </span>
                                    )}
                                </span>
                            }
                        />
                    )}
                    {ah != null && ah > 0 && <SummaryRow label="After hours" value={`$${ah.toFixed(2)}/hr`} />}
                    {ctx.trades.includes("backflow") && num(pricing.test_fee_flat) > 0 && (
                        <SummaryRow
                            label="Testing"
                            value={
                                <span className="inline-flex items-center gap-1">
                                    <Droplets className="h-3.5 w-3.5 text-muted-foreground" />$
                                    {num(pricing.test_fee_flat).toFixed(2)}
                                    {splitFirelineMain && num(pricing.test_fee_fireline_main) > 0 && (
                                        <span className="text-muted-foreground font-normal">
                                            · mains ${num(pricing.test_fee_fireline_main).toFixed(2)}
                                        </span>
                                    )}
                                </span>
                            }
                        />
                    )}
                    {ctx.trades.length > 0 && (
                        <SummaryRow
                            label="Materials"
                            value={
                                <span className="inline-flex items-center gap-1">
                                    <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                    {formatMaterialsLabel(pricing)}
                                </span>
                            }
                        />
                    )}
                    {ctx.trades.includes("backflow") && (
                        <SummaryRow
                            label="Fees"
                            value={
                                <span className="inline-flex flex-wrap gap-1 justify-end">
                                    <Receipt className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                                    {[
                                        adminFeeCharged && "Admin",
                                        cityFeesCharged && "City test/filing",
                                        permitFeesCharged !== false && "Permit",
                                    ]
                                        .filter(Boolean)
                                        .join(", ") || "None charged"}
                                </span>
                            }
                        />
                    )}
                    {ctx.trades.includes("fire_ext") && ctx.extPricing && (
                        <SummaryRow
                            label="Extinguishers"
                            value={
                                <span className="inline-flex items-center gap-1">
                                    <Flame className="h-3.5 w-3.5 text-muted-foreground" />$
                                    {num(ctx.extPricing.base_price).toFixed(0)} base ({ctx.extPricing.base_count} incl.)
                                    {num(ctx.extPricing.price_per_additional) > 0 && (
                                        <span className="text-muted-foreground font-normal">
                                            · ${num(ctx.extPricing.price_per_additional).toFixed(0)}/each &gt;
                                            {ctx.extPricing.base_count}
                                        </span>
                                    )}
                                </span>
                            }
                        />
                    )}
                    {(pricing.rate_agreed_on || pricing.rate_expires_on) && (
                        <SummaryRow
                            label="Rate agreement"
                            value={
                                <span className="inline-flex items-center gap-1 flex-wrap justify-end">
                                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                    {pricing.rate_agreed_on ? fmtDate(pricing.rate_agreed_on) : "—"}
                                    {pricing.rate_term_months != null && (
                                        <span className="text-muted-foreground font-normal">
                                            · {formatTermMonths(pricing.rate_term_months)}
                                        </span>
                                    )}
                                    {pricing.rate_expires_on && (
                                        <span className="text-muted-foreground font-normal">
                                            · expires {fmtDate(pricing.rate_expires_on)}
                                        </span>
                                    )}
                                </span>
                            }
                        />
                    )}
                    <p className="text-xs text-muted-foreground pt-3 border-t border-border/40 mt-2 leading-relaxed">
                        Changes apply to new jobs created after save. Open draft invoices may be updated in Close Out.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
